import * as React from "react";
import { IRcaTestResult, RcaTestStatus } from "../models/IRcaTestResult";
import { Icon, getTheme, ITheme, Stack, Link, useTheme } from "@fluentui/react";
import { useState } from "react";
import { CollapsePanel, ICollapsePanelStyles } from "./CollapsePanel";
import { Utils } from "../Utils";

/**
 * The props for the TestResultNode component
 */
interface ITestResultNodeProps {
  /**
   * The test result
   */
  result: IRcaTestResult;

  /**
   * A delegate to expand a certain node
   */
  setExpanded(node: IRcaTestResult, expanded: boolean): void;
}

/**
 * The icon names for each status
 */
export const iconNames: { [status: string]: string } = {
  Invalid: "inherit",
  Informational: "InfoSolid",
  Success: "CompletedSolid",
  SuccessWithWarnings: "WarningSolid",
  Warning: "WarningSolid",
  Error: "StatusErrorFull",
  FatalError: "StatusErrorFull",
};

/**
 * Gets the icon colors for each status.
 *
 * @param theme - The Fluent UI theme to derive colors from.
 * @returns The icon color map for the current theme.
 */
export const getIconColors = (theme: ITheme = getTheme()): { [status: string]: string } => {
  const { palette } = theme;
  return {
    Invalid: "inherit",
    Informational: palette.blue,
    Success: palette.green,
    SuccessWithWarnings: palette.yellowDark,
    Warning: palette.yellowDark,
    Error: palette.red,
    FatalError: palette.red,
    Skipped: palette.neutralSecondary,
    SuccessWithWarningsMid: palette.yellow,
  };
};

/**
 * Returns a string for the icon labels.
 *
 * @param status - The test status.
 * @returns - The string for the label.
 */
export const iconLabels = (status: RcaTestStatus): string => {
  switch (status) {
    case "Invalid":
    case "Error":
    case "FatalError":
      return ClientStrings.StepFailed;

    case "Success":
      return ClientStrings.StepSuccess;

    case "Warning":
      return ClientStrings.StepWarning;

    case "SuccessWithWarnings":
      return ClientStrings.StepSuccessWithWarnings;

    case "Informational":
      return ClientStrings.StepInformation;

    default:
      return "";
  }
};

/**
 * The test result node component
 *
 * @param props - The component props
 * @returns The markup
 */
export const TestResultNode: React.FC<ITestResultNodeProps> = ({ result, setExpanded }) => {
  const theme = useTheme();
  const iconColors = getIconColors(theme);
  const collapsePanelStyle: ICollapsePanelStyles = {
    button: {
      root: {
        backgroundColor: "transparent",
        color: theme.semanticColors.bodyText,
        borderRadius: 2,
        width: "auto",
        height: "auto",
      },
      rootHovered: {
        backgroundColor: theme.semanticColors.bodyBackgroundHovered,
        color: theme.semanticColors.bodyText,
      },
      rootPressed: {
        backgroundColor: theme.semanticColors.bodyBackgroundHovered,
        color: theme.semanticColors.bodyText,
      },
      rootFocused: {
        backgroundColor: theme.semanticColors.bodyBackgroundHovered,
        color: theme.semanticColors.bodyText,
      },
    },
    content: {
      root: {
        paddingLeft: 32,
        paddingRight: 32,
        selectors: {
          [Utils.mobileQuery]: {
            paddingLeft: 0,
          },
        },
      },
    },
  };

  const [expandDetails, setExpandDetails] = useState(result.Status === "Error" || result.Status === "FatalError");
  const hasChildren = result.Children.length > 0;
  const statusLabel = iconLabels(result.Status);
  const ariaLabel = statusLabel ? `${statusLabel}: ${result.TestDescription}` : result.TestDescription;

  return (
    <Stack horizontal role="treeitem" tabIndex={-1} aria-expanded={hasChildren ? !!result.expanded : undefined} aria-label={ariaLabel}>
      <Icon
        iconName={iconNames[result.Status]}
        aria-hidden="true"
        styles={{
          root: {
            color: iconColors[result.Status],
            fontSize: 16,
          },
        }}
      />
      <Stack styles={{ root: { width: "100%", paddingLeft: 5, paddingRight: 5 } }}>
        <div>{result.TestDescription}</div>
        <div>{result.ResultDescription}</div>
        {result.ContentUrl ? (
          <div>
            <Icon iconName="NavigateForward" aria-hidden="true" styles={{ root: { verticalAlign: "middle" } }} />
            &nbsp;
            <Link underline={true} href={result.ContentUrl} rel="noopener noreferrer" target="_blank">
              {WebsiteStrings.TestResultTable_TellMeMoreIssue}
            </Link>
          </div>
        ) : null}
        {result.AdditionalDetails ? (
          <CollapsePanel
            styles={collapsePanelStyle}
            renderTitleRow={(): React.ReactElement => <div>{WebsiteStrings.TestResultTable_AddtlDetails}</div>}
            expanded={expandDetails}
            setExpanded={(exp): void => setExpandDetails(exp)}
            titleText={WebsiteStrings.TestResultTable_AddtlDetails}
          >
            <div>{result.AdditionalDetails}</div>
          </CollapsePanel>
        ) : null}
        {hasChildren ? (
          <CollapsePanel
            styles={collapsePanelStyle}
            renderTitleRow={(): React.ReactElement => <div>{WebsiteStrings.TestResultTable_TestSteps}</div>}
            // !!result.expanded is used below because expanded can be null or undefined
            // Null or undefined results in false, true is true, false is false.
            expanded={!!result.expanded}
            setExpanded={(exp): void => setExpanded(result, exp)}
            titleText={WebsiteStrings.TestResultTable_TestSteps}
          >
            <div role="group">
              {result.Children.map((c) => (
                <TestResultNode key={c.key} result={c} setExpanded={setExpanded} />
              ))}
            </div>
          </CollapsePanel>
        ) : null}
      </Stack>
    </Stack>
  );
};
